import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Braces, LayoutList, RefreshCw, Radio, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import { policyRerunScan, testPolicyRailwayReload } from "@/lib/api";
import PolicyDocView from "./PolicyDocView";
import { EmergencyDialog } from "./PolicyDialogs";
import { fmtDate, who, JsonBlock, Stat } from "./policyShared";

function WebhookBadge({ r }) {
  if (!r) return <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-500">Railway: not notified</Badge>;
  if (r.ok) return <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-300" data-testid="policy-webhook-ok">Railway reloaded ({r.status})</Badge>;
  return <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-800 border-amber-300" title={r.error || r.response} data-testid="policy-webhook-failed">Railway webhook: {r.error ? "skipped / failed" : `HTTP ${r.status}`} — 5-min poll fallback</Badge>;
}

function RailwayTools({ canEdit, onChanged }) {
  const [busy, setBusy] = useState("");
  if (!canEdit) return null;
  const ping = async (target = "railway") => {
    setBusy(target === "sim" ? "sim" : "ping");
    try {
      const r = await testPolicyRailwayReload(target);
      const w = r.webhook_result || {};
      const label = target === "sim" ? "Simulator" : "Railway";
      if (w.ok) toast.success(`${label} reloaded ${r.version_label} (HTTP ${w.status})${target === "sim" ? " — same module Railway will run" : ""}`);
      else toast.warning(`${label} reload not confirmed: ${w.error || `HTTP ${w.status} ${w.response || ""}`}`, { duration: 9000 });
      if (target !== "sim") onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); } finally { setBusy(""); }
  };
  const scan = async () => {
    setBusy("scan");
    try {
      const r = await policyRerunScan(false);
      toast.success(`Re-run scan: ${r.marked.length} deal${r.marked.length === 1 ? "" : "s"} marked "Re-run needed" (${r.skipped_terminal.length} finished deals skipped${r.errors.length ? `, ${r.errors.length} errors` : ""})`, { duration: 9000 });
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); } finally { setBusy(""); }
  };
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => ping("railway")} disabled={!!busy} data-testid="policy-railway-test-btn"><Radio className="w-4 h-4 mr-1" /> {busy === "ping" ? "Sending…" : "Test Railway reload"}</Button>
      <Button size="sm" variant="outline" onClick={() => ping("sim")} disabled={!!busy} title="Send the signed reload webhook to the in-portal Railway simulator (the exact module Railway adopts) to prove the round trip" data-testid="policy-railway-sim-btn"><FlaskConical className="w-4 h-4 mr-1" /> {busy === "sim" ? "Sending…" : "Test via simulator"}</Button>
      <Button size="sm" variant="outline" onClick={scan} disabled={!!busy} title="Flag live-pipeline deals whose last policy run used an older version" data-testid="policy-rerun-scan-btn"><RefreshCw className="w-4 h-4 mr-1" /> {busy === "scan" ? "Scanning…" : "Mark re-runs needed"}</Button>
    </>
  );
}

export default function PolicyOverview({ live, liveMeta, schema, groups, canApprove, canEdit, onChanged }) {
  const [group, setGroup] = useState("product");
  const [raw, setRaw] = useState(false);
  const [emergency, setEmergency] = useState(false);
  const missing = useMemo(() => new Set(live?.required_before_publish || []), [live]);
  const missingByGroup = useMemo(() => {
    const m = {};
    for (const p of live?.required_before_publish || []) { const g = p.split(/[.[]/)[0]; m[g] = (m[g] || 0) + 1; }
    return m;
  }, [live]);
  if (!live || !schema) return null;
  return (
    <div className="space-y-4" data-testid="policy-overview">
      <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-4 flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-2xl font-bold text-emerald-700 font-mono" data-testid="policy-live-label">{live.version_label}</span>
            <Badge className="bg-emerald-600 hover:bg-emerald-600">LIVE</Badge>
            {liveMeta?.emergency && <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-300">emergency publish</Badge>}
            {liveMeta?.rollback_of && <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300">rollback of {liveMeta.rollback_of}</Badge>}
            <WebhookBadge r={liveMeta?.webhook_result} />
          </div>
          <div className="text-xs text-slate-600" data-testid="policy-live-meta">
            Published {fmtDate(live.published_at)} by {who(liveMeta, "published_by")} · approved by {who(liveMeta, "approved_by")} · schema {live.schema_version} · evaluator {schema.evaluator_version}
          </div>
          <div className="text-[11px] text-slate-400 font-mono break-all">ETag {live.etag} · id {live.id}</div>
        </div>
        <div className="flex flex-wrap gap-2 items-start">
          <Stat label="Changes in this version" value={liveMeta?.change_log?.length ?? 0} testId="policy-live-changes" />
          <Stat label="Required still blank" value={missing.size} tone={missing.size ? "text-amber-700" : "text-emerald-700"} testId="policy-live-missing" />
          <RailwayTools canEdit={canEdit || canApprove} onChanged={onChanged} />
          {canApprove && <Button size="sm" variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => setEmergency(true)} data-testid="policy-emergency-btn"><AlertTriangle className="w-4 h-4 mr-1" /> Emergency change</Button>}
        </div>
      </div>
      {missing.size > 0 && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2 flex gap-2" data-testid="policy-missing-banner">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span>{missing.size} bracketed [__] settings from the procedure are still blank (highlighted below). They do not block v1.x publishes but every one must be filled before v2.0 can go live.</span>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-[230px_1fr] gap-4">
        <nav className="rounded-lg border border-slate-200 bg-white p-2 space-y-0.5 h-fit lg:sticky lg:top-4" data-testid="policy-group-nav">
          {groups.map((g) => (
            <button key={g.key} onClick={() => setGroup(g.key)} className={`w-full text-left text-sm px-2.5 py-1.5 rounded-md flex items-center justify-between transition-colors ${group === g.key ? "bg-emerald-600 text-white" : "text-slate-700 hover:bg-slate-100"}`} data-testid={`policy-group-${g.key}`}>
              <span>{g.title}</span>
              {missingByGroup[g.key] ? <span className={`text-[10px] rounded-full px-1.5 ${group === g.key ? "bg-white/20" : "bg-amber-100 text-amber-800"}`}>{missingByGroup[g.key]}</span> : null}
            </button>
          ))}
        </nav>
        <div className="space-y-2 min-w-0">
          <div className="flex justify-end">
            <div className="inline-flex rounded-md border border-slate-300 overflow-hidden text-xs">
              <button onClick={() => setRaw(false)} className={`px-2.5 py-1 flex items-center gap-1 ${!raw ? "bg-slate-800 text-white" : "bg-white text-slate-600"}`} data-testid="policy-view-settings"><LayoutList className="w-3.5 h-3.5" /> Settings</button>
              <button onClick={() => setRaw(true)} className={`px-2.5 py-1 flex items-center gap-1 ${raw ? "bg-slate-800 text-white" : "bg-white text-slate-600"}`} data-testid="policy-view-json"><Braces className="w-3.5 h-3.5" /> JSON</button>
            </div>
          </div>
          {raw ? <JsonBlock value={live.document?.[group]} testId="policy-group-json" /> : <PolicyDocView schema={schema.schema} doc={live.document} group={group} missing={missing} />}
        </div>
      </div>
      <EmergencyDialog open={emergency} onClose={() => setEmergency(false)} onDone={onChanged} />
    </div>
  );
}
